import type { FC, FormEvent, ReactNode } from 'react'
import type { AttendanceStatus, GradeType, JournalEntry } from '@/types'
import type { SheetCell } from '@/lib/spreadsheet'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { average, compareText, formatDecimal } from '@/lib/academic'
import { DEMO_TODAY, dayNumber, formatDate, formatMonth } from '@/lib/dates'
import { downloadWordTable, downloadXlsx, printPage } from '@/lib/spreadsheet'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { TabCards } from '@/components/app/report-tabs'
import { StatTile } from '@/components/app/stat-tile'
import { StatusCode } from '@/components/app/status'
import { useAcademic } from '@/components/providers/academic-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { DEFAULT_KKM, GRADE_TYPE_LABELS, GRADE_TYPES } from '@/constants/academic'

import {
    Check,
    ChevronDown,
    CircleCheck,
    Clock,
    Download,
    FileText,
    Filter,
    NotebookPen,
    Printer,
    Target,
    TrendingUp,
    Users
} from 'lucide-react'

type ViewType = 'absensi' | 'nilai' | 'jurnal'

type Period = 'bulan' | 'semester' | 'tahun'

type FilterValues = {
    kelas: string
    mapel: string
    periode: Period
    bulan: string
    semester: string
    tahun: string
}

type GradeColumn = {
    key: string
    type: GradeType
    label: string
    sequence: number
    date: string
}

const VIEW_COPY: Record<ViewType, { eyebrow: string, title: string, docTitle: string }> = {
    absensi: { eyebrow: 'Laporan kehadiran', title: 'Rekap Absensi Siswa', docTitle: 'Rekap Absensi Siswa' },
    nilai: { eyebrow: 'Laporan hasil belajar', title: 'Rekap Nilai Siswa', docTitle: 'Rekap Nilai Siswa' },
    jurnal: { eyebrow: 'Laporan jurnal mengajar', title: 'Jurnal Mengajar Bulanan', docTitle: 'Rekap Jurnal Mengajar' }
}

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

const thClass = 'border-b border-r border-border/70 px-3 py-2.5 font-semibold whitespace-nowrap last:border-r-0'
const tdClass = 'border-r border-border/60 px-3 py-2.5 last:border-r-0'

type ReportFilterFormProps = {
    viewType: ViewType
    initial: FilterValues
    periods: { value: string, label: string }[]
    years: { value: string, label: string }[]
    onApply: (values: FilterValues) => void
}

const ReportFilterForm: FC<ReportFilterFormProps> = ({ viewType, initial, periods, years, onApply }): ReactNode => {
    const { classes, subjects } = useAcademic()
    const [values, setValues] = useState<FilterValues>(initial)

    const update = (patch: Partial<FilterValues>): void => setValues(current => ({ ...current, ...patch }))

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        onApply(values)
    }

    const monthLabel = viewType === 'absensi' ? 'Periode absensi' : viewType === 'nilai' ? 'Periode nilai' : 'Periode jurnal'

    return (
        <Card className='mb-6 p-4 sm:p-5 print:hidden'>
            <form onSubmit={handleSubmit} className='grid gap-4 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))_auto] xl:items-end'>
                <Field label='Kelas' htmlFor='reportClass'>
                    <Select id='reportClass' value={values.kelas} onChange={event => update({ kelas: event.target.value })}>
                        {viewType === 'jurnal' && <option value=''>Semua kelas</option>}
                        {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                </Field>

                {viewType === 'nilai' && (
                    <Field label='Mata pelajaran' htmlFor='reportSubject'>
                        <Select id='reportSubject' value={values.mapel} onChange={event => update({ mapel: event.target.value })}>
                            {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </Field>
                )}

                <Field label='Periode' htmlFor='reportPeriod'>
                    <Select id='reportPeriod' value={values.periode} onChange={event => update({ periode: event.target.value as Period })}>
                        <option value='bulan'>Bulanan</option>
                        <option value='semester'>Semester</option>
                        <option value='tahun'>Tahunan</option>
                    </Select>
                </Field>

                {values.periode === 'bulan' && (
                    <Field label={monthLabel} htmlFor='reportMonth'>
                        <Input id='reportMonth' type='month' value={values.bulan} onChange={event => update({ bulan: event.target.value })} />
                    </Field>
                )}

                {values.periode === 'semester' && (
                    <Field label='Semester' htmlFor='reportSemester'>
                        <Select id='reportSemester' value={values.semester} onChange={event => update({ semester: event.target.value })}>
                            {periods.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </Select>
                    </Field>
                )}

                {values.periode === 'tahun' && (
                    <Field label='Tahun ajaran' htmlFor='reportYear'>
                        <Select id='reportYear' value={values.tahun} onChange={event => update({ tahun: event.target.value })}>
                            {years.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </Select>
                    </Field>
                )}

                <Button type='submit' variant='outline' className='xl:col-start-5'>
                    <Filter aria-hidden='true' />
                    Terapkan filter
                </Button>
            </form>
        </Card>
    )
}

const ReportsPage: FC = (): ReactNode => {
    const { data, classes, subjects, students, attendance, grades, journals, classById, subjectById } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const toast = useToast()

    if (!ready) return <PageSkeleton />

    const requestedView = get('view')
    const viewType: ViewType = requestedView === 'nilai' || requestedView === 'jurnal' ? requestedView : 'absensi'

    const requestedClass = get('kelas')
    const classValid = classes.some(item => item.id === requestedClass)
    const classId = viewType === 'jurnal' ? (classValid ? requestedClass as string : '') : (classValid ? requestedClass as string : classes[0]?.id ?? '')
    const subjectId = subjects.some(item => item.id === get('mapel')) ? get('mapel') as string : subjects[0]?.id ?? ''
    const subject = subjectById.get(subjectId)

    const requestedPeriod = get('periode')
    const period: Period = requestedPeriod === 'semester' || requestedPeriod === 'tahun' ? requestedPeriod : 'bulan'

    const todayYear = Number(DEMO_TODAY.slice(0, 4))
    const todayMonth = Number(DEMO_TODAY.slice(5, 7))
    const academicStart = todayMonth >= 7 ? todayYear : todayYear - 1

    const monthParam = get('bulan') ?? ''
    const month = MONTH_PATTERN.test(monthParam) ? monthParam : DEMO_TODAY.slice(0, 7)
    const semesterParam = get('semester') ?? ''
    const semester = /^\d{4}-(1|2)$/.test(semesterParam) ? semesterParam : `${academicStart}-${todayMonth >= 7 ? 1 : 2}`
    const yearParam = get('tahun') ?? ''
    const year = /^\d{4}$/.test(yearParam) ? yearParam : String(academicStart)

    let start = `${month}-01`
    let end = (() => {
        const [y, m] = month.split('-').map(Number)

        return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
    })()
    let periodLabel = formatMonth(month)
    let periodKey = month

    if (period === 'semester') {
        const [semesterYear, semesterNumber] = semester.split('-').map(Number)

        start = semesterNumber === 1 ? `${semesterYear}-07-01` : `${semesterYear + 1}-01-01`
        end = semesterNumber === 1 ? `${semesterYear + 1}-01-01` : `${semesterYear + 1}-07-01`
        periodLabel = `${semesterYear}/${semesterYear + 1} Semester ${semesterNumber}`
        periodKey = semester
    }

    if (period === 'tahun') {
        start = `${year}-07-01`
        end = `${Number(year) + 1}-07-01`
        periodLabel = `${year}/${Number(year) + 1}`
        periodKey = year
    }

    const inRange = (date: string): boolean => date >= start && date < end

    const academicPeriods = Array.from(new Map(
        [...data.years]
            .sort((a, b) => b.name.localeCompare(a.name) || a.semester.localeCompare(b.semester))
            .map(item => [`${item.name.slice(0, 4)}-${item.semester}`, `${item.name} Semester ${item.semester}`] as [string, string])
    )).map(([value, label]) => ({ value, label }))

    const academicYears = Array.from(new Set([...data.years].sort((a, b) => b.name.localeCompare(a.name)).map(item => item.name)))
        .map(name => ({ value: name.slice(0, 4), label: name }))

    const className = classById.get(classId)?.name ?? ''
    const classStudents = students.filter(student => student.classId === classId)

    // Jurnal
    const journalRows = viewType === 'jurnal'
        ? journals
            .filter(entry => inRange(entry.date) && (!classId || entry.classId === classId))
            .map(entry => {
                const records = attendance.filter(record => record.classId === entry.classId && record.date === entry.date)
                const count = (status: AttendanceStatus): number => records.filter(record => record.status === status).length

                return { entry, className: classById.get(entry.classId)?.name ?? '-', S: count('S'), I: count('I'), A: count('A') }
            })
            .sort((a, b) => a.entry.date.localeCompare(b.entry.date) || compareText(a.className, b.className) || compareText(a.entry.period, b.entry.period))
        : []

    const journalAttendance = viewType === 'jurnal'
        ? attendance.filter(record => inRange(record.date) && (!classId || record.classId === classId))
        : []

    // Absensi
    const attendanceRecords = viewType === 'absensi'
        ? attendance.filter(record => record.classId === classId && inRange(record.date))
        : []

    const attendanceDates = Array.from(new Set(attendanceRecords.map(record => record.date))).sort()
    const attendanceValues = new Map(attendanceRecords.map(record => [`${record.studentId}|${record.date}`, record.status]))

    const attendanceRows = classStudents.map(student => {
        const own = attendance.filter(record => record.studentId === student.id && inRange(record.date))
        const count = (status: AttendanceStatus): number => own.filter(record => record.status === status).length

        return { student, H: count('H'), S: count('S'), I: count('I'), A: count('A') }
    })

    // Nilai
    const classStudentIds = new Set(classStudents.map(student => student.id))
    const gradeEntries = viewType === 'nilai'
        ? grades
            .filter(record => classStudentIds.has(record.studentId) && record.subjectId === subjectId && inRange(record.date))
            .sort((a, b) => GRADE_TYPES.indexOf(a.type) - GRADE_TYPES.indexOf(b.type) || a.date.localeCompare(b.date) || a.id.localeCompare(b.id))
        : []

    const gradeColumns: GradeColumn[] = []
    const gradeValues = new Map<string, number>()
    const sequences: Partial<Record<GradeType, number>> = {}

    gradeEntries.forEach(record => {
        const key = `${record.type}:${record.date}`

        if (!gradeColumns.some(column => column.key === key)) {
            const sequence = (sequences[record.type] ?? 0) + 1

            sequences[record.type] = sequence
            gradeColumns.push({ key, type: record.type, label: `${GRADE_TYPE_LABELS[record.type]} ${sequence}`, sequence, date: record.date })
        }

        gradeValues.set(`${record.studentId}|${key}`, record.score)
    })

    const gradeGroups = GRADE_TYPES
        .map(type => ({ type, columns: gradeColumns.filter(column => column.type === type) }))
        .filter(group => group.columns.length > 0)

    const gradeRows = classStudents.map(student => {
        const scores = gradeEntries.filter(record => record.studentId === student.id).map(record => record.score)

        return { student, mean: average(scores), total: scores.length }
    })

    const kkm = subject?.kkm ?? DEFAULT_KKM

    // Summary numbers, exactly as the original page computes them
    const presentTotal = attendanceRows.reduce((sum, row) => sum + row.H, 0)
    const notPresentTotal = attendanceRows.reduce((sum, row) => sum + row.S + row.I + row.A, 0)
    const recordsTotal = gradeRows.reduce((sum, row) => sum + row.total, 0)
    const classAverage = average(gradeRows.filter(row => row.mean !== null).map(row => row.mean as number))
    const journalSick = journalAttendance.filter(record => record.status === 'S').length
    const journalPermission = journalAttendance.filter(record => record.status === 'I').length
    const journalAbsent = journalAttendance.filter(record => record.status === 'A').length

    const copy = VIEW_COPY[viewType]
    const periodSuffix = period === 'semester'
        ? `untuk periode ${periodLabel} (Per semester)`
        : period === 'tahun' ? `untuk periode ${periodLabel} (Per tahun)` : `untuk periode ${periodLabel}.`

    const tableDescription = viewType === 'absensi'
        ? `Jumlah status kehadiran per siswa ${periodSuffix}`
        : viewType === 'nilai'
            ? `Ringkasan nilai ${subject?.name ?? 'siswa'} ${periodSuffix}`
            : `Materi, jam pelajaran, dan status kehadiran setiap pertemuan ${periodSuffix}`

    const rowCount = viewType === 'jurnal' ? journalRows.length : classStudents.length

    // Rows shared by the Excel and Word exports
    const buildExportRows = (): SheetCell[][] => {
        const head = (value: string, extra: Partial<NonNullable<SheetCell>> = {}): SheetCell => ({ value, header: true, ...extra })
        const blanks = (count: number): SheetCell[] => Array.from({ length: Math.max(0, count) }, () => null)

        if (viewType === 'jurnal') {
            return [
                [head('No.', { rowSpan: 2 }), head('Tanggal', { rowSpan: 2 }), head('Jam ke', { rowSpan: 2 }), head('Kelas', { rowSpan: 2 }), head('Materi pembelajaran', { rowSpan: 2 }), head('Rekap absen', { columnSpan: 3 }), null, null],
                [...blanks(5), head('Sakit'), head('Izin'), head('Alpha')],
                ...journalRows.map((row, index) => [
                    { value: index + 1 },
                    { value: formatDate(row.entry.date, 'short') },
                    { value: `${row.entry.period} · ${row.entry.start}–${row.entry.end}` },
                    { value: row.className },
                    { value: row.entry.material },
                    { value: row.S },
                    { value: row.I },
                    { value: row.A }
                ])
            ]
        }

        if (viewType === 'absensi') {
            const dateCount = Math.max(1, attendanceDates.length)

            return [
                [
                    head('No', { rowSpan: 2 }), head('NIS', { rowSpan: 2 }), head('Nama', { rowSpan: 2 }), head('Kelas', { rowSpan: 2 }),
                    attendanceDates.length > 0 ? head('Tanggal', { columnSpan: dateCount }) : head('Tanggal', { rowSpan: 2 }),
                    ...blanks(dateCount - 1),
                    head('Jumlah', { columnSpan: 4 }), null, null, null
                ],
                [
                    ...blanks(4),
                    ...(attendanceDates.length > 0 ? attendanceDates.map(date => head(dayNumber(date))) : [null]),
                    head('H'), head('S'), head('I'), head('A')
                ],
                ...attendanceRows.map((row, index) => [
                    { value: index + 1 },
                    { value: row.student.nis },
                    { value: row.student.name },
                    { value: className },
                    ...(attendanceDates.length > 0
                        ? attendanceDates.map(date => ({ value: attendanceValues.get(`${row.student.id}|${date}`) ?? '—', align: 'center' as const }))
                        : [{ value: '—', align: 'center' as const }]),
                    { value: row.H },
                    { value: row.S },
                    { value: row.I },
                    { value: row.A }
                ])
            ]
        }

        return [
            [
                head('No', { rowSpan: 2 }), head('NIS', { rowSpan: 2 }), head('Nama', { rowSpan: 2 }), head('Kelas', { rowSpan: 2 }),
                ...gradeGroups.flatMap(group => [head(GRADE_TYPE_LABELS[group.type], { columnSpan: group.columns.length }), ...blanks(group.columns.length - 1)]),
                head('Rata-rata', { rowSpan: 2 }), head('KKM', { rowSpan: 2 })
            ],
            [...blanks(4), ...gradeColumns.map(column => head(String(column.sequence))), null, null],
            ...gradeRows.map((row, index) => [
                { value: index + 1 },
                { value: row.student.nis },
                { value: row.student.name },
                { value: className },
                ...gradeColumns.map(column => ({ value: gradeValues.get(`${row.student.id}|${column.key}`) ?? '—', align: 'center' as const })),
                { value: row.mean === null ? '—' : Number(row.mean.toFixed(1)) },
                { value: Math.round(kkm) }
            ])
        ]
    }

    const fileBase = `rekap-${viewType}-${slugify(className || 'semua-kelas')}-${periodKey}`

    const exportExcel = async (): Promise<void> => {
        try {
            await downloadXlsx(`${fileBase}.xlsx`, buildExportRows(), { sheet: copy.docTitle.slice(0, 31), headerRows: 2 })
        }
        catch {
            toast.error('Ekspor gagal', 'Coba unduh sekali lagi.')
        }
    }

    const exportWord = (): void => {
        downloadWordTable(`${fileBase}.doc`, `${copy.docTitle} · ${periodLabel}`, `${className ? `Kelas ${className}` : 'Semua kelas'}${viewType === 'nilai' && subject ? ` · ${subject.name}` : ''}`, buildExportRows())
    }

    const exportPdf = (): void => {
        toast.info('Simpan sebagai PDF', 'Pilih “Simpan sebagai PDF” pada jendela cetak yang terbuka.')
        window.setTimeout(printPage, 300)
    }

    const tabHref = (view: ViewType): string => {
        const params = new URLSearchParams({ view })

        if (classId) params.set('kelas', classId)
        if (view === 'nilai' && subjectId) params.set('mapel', subjectId)
        params.set('bulan', month)

        return `/rekap?${params.toString()}`
    }

    return (
        <>
            <Seo title='Rekap akademik' />

            <PageHeader
                eyebrow='Pusat laporan'
                title='Rekap akademik'
                description='Rekap nilai, absensi bulanan, dan jurnal mengajar bulanan.'
            />

            <TabCards
                label='Jenis rekap'
                items={[
                    { href: tabHref('absensi'), label: 'Absen per Bulan', description: 'Ringkasan status kehadiran per siswa', icon: <Check />, active: viewType === 'absensi' },
                    { href: tabHref('nilai'), label: 'Rekap Nilai', description: 'Hasil penilaian per mata pelajaran', icon: <TrendingUp />, active: viewType === 'nilai' },
                    { href: tabHref('jurnal'), label: 'Jurnal Mengajar Bulanan', description: 'Materi dan kehadiran per pertemuan', icon: <NotebookPen />, active: viewType === 'jurnal' }
                ]}
            />

            <ReportFilterForm
                key={`${viewType}-${classId}-${subjectId}-${period}-${month}-${semester}-${year}`}
                viewType={viewType}
                initial={{ kelas: classId, mapel: subjectId, periode: period, bulan: month, semester, tahun: year }}
                periods={academicPeriods}
                years={academicYears}
                onApply={values => set({
                    view: viewType,
                    kelas: values.kelas || null,
                    mapel: viewType === 'nilai' ? values.mapel : null,
                    periode: values.periode,
                    bulan: MONTH_PATTERN.test(values.bulan) ? values.bulan : month,
                    semester: values.periode === 'semester' ? values.semester : null,
                    tahun: values.periode === 'tahun' ? values.tahun : null
                })}
            />

            <section aria-label='Ringkasan rekap' className='mb-6 grid gap-4 md:grid-cols-3'>
                {viewType === 'jurnal' ? (
                    <>
                        <StatTile icon={<NotebookPen />} tone='primary' label='Jurnal tersimpan' value={journalRows.length} hint={periodLabel} />
                        <StatTile icon={<Clock />} tone='warning' label='Sakit' value={journalSick} hint='Keterangan siswa tidak hadir' />
                        <StatTile icon={<Clock />} tone='danger' label='Izin / Alpha' value={journalPermission + journalAbsent} hint={`Izin ${journalPermission} · Alpha ${journalAbsent}`} />
                    </>
                ) : (
                    <>
                        <StatTile icon={<Users />} tone='primary' label='Siswa di kelas' value={classStudents.length} hint={className || 'Kelas terpilih'} />

                        {viewType === 'absensi' ? (
                            <>
                                <StatTile
                                    icon={<CircleCheck />}
                                    tone='success'
                                    label='Total hadir'
                                    value={presentTotal}
                                    hint={`${presentTotal + notPresentTotal > 0 ? Math.round((presentTotal * 100) / (presentTotal + notPresentTotal)) : 0}% dari status tercatat`}
                                />
                                <StatTile icon={<Clock />} tone='warning' label='Total tidak hadir' value={notPresentTotal} hint={`${periodLabel} · kelas terpilih`} />
                            </>
                        ) : (
                            <>
                                <StatTile icon={<CircleCheck />} tone='success' label='Nilai tercatat' value={recordsTotal} hint={subject?.name ?? 'Semua mapel'} />
                                <StatTile
                                    icon={<Target />}
                                    tone='warning'
                                    label='Rata-rata kelas'
                                    value={classAverage === null ? '—' : formatDecimal(classAverage, 1)}
                                    hint={`KKM ${Math.round(kkm)} · periode ${periodLabel}`}
                                />
                            </>
                        )}
                    </>
                )}
            </section>

            <Card>
                <header className='flex flex-col gap-4 border-b px-5 py-4 lg:flex-row lg:items-start lg:justify-between'>
                    <div className='min-w-0'>
                        <p className='text-[11px] font-semibold tracking-[0.12em] text-primary uppercase'>
                            {copy.eyebrow}
                        </p>

                        <h2 className='mt-0.5 text-[15px] font-semibold text-foreground'>
                            {copy.title}
                        </h2>

                        <p className='mt-0.5 text-[13px] text-muted-foreground'>
                            {tableDescription}
                        </p>
                    </div>

                    <div className='flex shrink-0 flex-wrap gap-2 print:hidden'>
                        <Button variant='outline' size='sm' onClick={() => void exportExcel()}>
                            <Download aria-hidden='true' />
                            Unduh Excel (.xlsx)
                        </Button>

                        <DropdownMenu
                            label='Ekspor lainnya'
                            triggerClassName='inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-3 text-[13px] font-semibold text-foreground shadow-card transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-3 focus-visible:ring-ring/40 dark:bg-input/20'
                            trigger={(
                                <>
                                    Ekspor lainnya
                                    <ChevronDown className='size-4' aria-hidden='true' />
                                </>
                            )}
                            items={[
                                { label: 'Cetak', description: 'Buka dialog cetak browser', icon: <Printer />, onSelect: printPage },
                                { label: 'Unduh PDF (.pdf)', icon: <FileText />, onSelect: exportPdf },
                                { label: 'Unduh Word (.doc)', icon: <FileText />, onSelect: exportWord, separatorBefore: true }
                            ]}
                        />
                    </div>
                </header>

                <div className='overflow-x-auto'>
                    <table className='w-full border-collapse text-left text-sm'>
                        <thead className='bg-muted/55 text-[11px] tracking-[0.04em] text-muted-foreground uppercase'>
                            {viewType === 'jurnal' && (
                                <>
                                    <tr>
                                        <th rowSpan={2} className={thClass}>No.</th>
                                        <th rowSpan={2} className={thClass}>Tanggal</th>
                                        <th rowSpan={2} className={thClass}>Jam ke</th>
                                        <th rowSpan={2} className={thClass}>Kelas</th>
                                        <th rowSpan={2} className={cn(thClass, 'min-w-64')}>Materi pembelajaran</th>
                                        <th colSpan={3} className={cn(thClass, 'text-center')}>Rekap absen</th>
                                    </tr>
                                    <tr>
                                        <th className={cn(thClass, 'text-center')}>Sakit</th>
                                        <th className={cn(thClass, 'text-center')}>Izin</th>
                                        <th className={cn(thClass, 'text-center')}>Alpha</th>
                                    </tr>
                                </>
                            )}

                            {viewType === 'absensi' && (
                                <>
                                    <tr>
                                        <th rowSpan={2} className={thClass}>No.</th>
                                        <th rowSpan={2} className={thClass}>NIS</th>
                                        <th rowSpan={2} className={thClass}>Nama</th>
                                        <th rowSpan={2} className={thClass}>Kelas</th>
                                        {attendanceDates.length > 0
                                            ? <th colSpan={attendanceDates.length} className={cn(thClass, 'text-center')}>Tanggal</th>
                                            : <th rowSpan={2} className={thClass}>Tanggal</th>}
                                        <th colSpan={4} className={cn(thClass, 'text-center')}>Jumlah</th>
                                    </tr>
                                    <tr>
                                        {attendanceDates.map(date => (
                                            <th key={date} className={cn(thClass, 'min-w-9 px-1 text-center tabular-nums')} title={formatDate(date, 'long')}>
                                                {dayNumber(date)}
                                            </th>
                                        ))}
                                        <th className={cn(thClass, 'text-center text-success')}>H</th>
                                        <th className={cn(thClass, 'text-center text-warning')}>S</th>
                                        <th className={cn(thClass, 'text-center text-info')}>I</th>
                                        <th className={cn(thClass, 'text-center text-destructive')}>A</th>
                                    </tr>
                                </>
                            )}

                            {viewType === 'nilai' && (
                                <>
                                    <tr>
                                        <th rowSpan={2} className={thClass}>No.</th>
                                        <th rowSpan={2} className={thClass}>NIS</th>
                                        <th rowSpan={2} className={thClass}>Nama</th>
                                        <th rowSpan={2} className={thClass}>Kelas</th>
                                        {gradeGroups.map(group => (
                                            <th key={group.type} colSpan={group.columns.length} className={cn(thClass, 'text-center')}>
                                                {GRADE_TYPE_LABELS[group.type]}
                                            </th>
                                        ))}
                                        <th rowSpan={2} className={cn(thClass, 'text-center')}>Rata-rata</th>
                                        <th rowSpan={2} className={cn(thClass, 'text-center')}>KKM</th>
                                    </tr>
                                    <tr>
                                        {gradeColumns.map(column => (
                                            <th key={column.key} className={cn(thClass, 'min-w-11 text-center tabular-nums')} title={formatDate(column.date, 'long')}>
                                                {column.sequence}
                                            </th>
                                        ))}
                                    </tr>
                                </>
                            )}
                        </thead>

                        <tbody>
                            {viewType === 'jurnal' && journalRows.map((row, index) => (
                                <tr key={row.entry.id} className='border-b border-border/70 last:border-0 hover:bg-muted/30'>
                                    <td className={cn(tdClass, 'text-muted-foreground tabular-nums')}>{index + 1}</td>
                                    <td className={cn(tdClass, 'whitespace-nowrap tabular-nums')}>{formatDate(row.entry.date, 'short')}</td>
                                    <td className={cn(tdClass, 'whitespace-nowrap tabular-nums')}>{journalTime(row.entry)}</td>
                                    <td className={cn(tdClass, 'font-semibold whitespace-nowrap text-foreground')}>{row.className}</td>
                                    <td className={cn(tdClass, 'leading-relaxed')}>{row.entry.material}</td>
                                    <td className={cn(tdClass, 'text-center tabular-nums')}>{row.S}</td>
                                    <td className={cn(tdClass, 'text-center tabular-nums')}>{row.I}</td>
                                    <td className={cn(tdClass, 'text-center tabular-nums', row.A > 0 && 'font-semibold text-destructive')}>{row.A}</td>
                                </tr>
                            ))}

                            {viewType === 'absensi' && attendanceRows.map((row, index) => (
                                <tr key={row.student.id} className='border-b border-border/70 last:border-0 hover:bg-muted/30'>
                                    <td className={cn(tdClass, 'text-muted-foreground tabular-nums')}>{index + 1}</td>
                                    <td className={cn(tdClass, 'font-mono text-xs text-muted-foreground')}>{row.student.nis}</td>
                                    <td className={cn(tdClass, 'font-semibold whitespace-nowrap text-foreground')}>{row.student.name}</td>
                                    <td className={cn(tdClass, 'whitespace-nowrap')}>{className}</td>
                                    {attendanceDates.length > 0
                                        ? attendanceDates.map(date => (
                                            <td key={date} className={cn(tdClass, 'px-1 py-1.5 text-center')}>
                                                <StatusCode status={attendanceValues.get(`${row.student.id}|${date}`)} />
                                            </td>
                                        ))
                                        : <td className={cn(tdClass, 'text-center text-muted-foreground')}>—</td>}
                                    <td className={cn(tdClass, 'text-center font-semibold tabular-nums')}>{row.H}</td>
                                    <td className={cn(tdClass, 'text-center font-semibold tabular-nums')}>{row.S}</td>
                                    <td className={cn(tdClass, 'text-center font-semibold tabular-nums')}>{row.I}</td>
                                    <td className={cn(tdClass, 'text-center font-semibold tabular-nums')}>{row.A}</td>
                                </tr>
                            ))}

                            {viewType === 'nilai' && gradeRows.map((row, index) => (
                                <tr key={row.student.id} className='border-b border-border/70 last:border-0 hover:bg-muted/30'>
                                    <td className={cn(tdClass, 'text-muted-foreground tabular-nums')}>{index + 1}</td>
                                    <td className={cn(tdClass, 'font-mono text-xs text-muted-foreground')}>{row.student.nis}</td>
                                    <td className={cn(tdClass, 'font-semibold whitespace-nowrap text-foreground')}>{row.student.name}</td>
                                    <td className={cn(tdClass, 'whitespace-nowrap')}>{className}</td>
                                    {gradeColumns.map(column => {
                                        const score = gradeValues.get(`${row.student.id}|${column.key}`)

                                        return (
                                            <td
                                                key={column.key}
                                                className={cn(tdClass, 'text-center tabular-nums', score !== undefined && score < kkm && 'font-semibold text-destructive')}
                                            >
                                                {score === undefined ? <span className='text-muted-foreground/60'>—</span> : formatDecimal(score, 2, true)}
                                            </td>
                                        )
                                    })}
                                    <td className={cn(tdClass, 'text-center font-bold tabular-nums')}>{row.mean === null ? '—' : formatDecimal(row.mean, 1)}</td>
                                    <td className={cn(tdClass, 'text-center text-muted-foreground tabular-nums')}>{Math.round(kkm)}</td>
                                </tr>
                            ))}

                            {rowCount === 0 && (
                                <tr>
                                    <td colSpan={40} className='px-5 py-12 text-center text-sm text-muted-foreground'>
                                        Belum ada data pada periode ini.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {viewType === 'jurnal' && (
                    <p className='border-t px-5 py-3 text-xs text-muted-foreground'>
                        Keterangan hanya ditampilkan untuk siswa tidak hadir: Sakit, Izin, dan Alpha.
                    </p>
                )}
            </Card>
        </>
    )
}

const journalTime = (entry: JournalEntry): string => `${entry.period} · ${entry.start}–${entry.end}`

const slugify = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

export default ReportsPage
